import type { BrushId, DrawOperation, NormPoint, OperationType, RegionId } from './types';
import { REGION_IDS } from './types';

/**
 * Operation construction, serialization and validation.
 *
 * Operations are the append-only source of truth. Serialization is a plain,
 * versionless JSON projection (the document carries the schema version). All
 * validation here is defensive so corrupted or foreign data can never crash a
 * replay — see `isValidOperation`.
 */

/** Pluggable id generator (kept injectable so tests can be deterministic). */
export type IdFactory = () => string;

let counter = 0;
export const defaultIdFactory: IdFactory = () => {
  // Prefer a real UUID when available; fall back to a stable counter+time id.
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  counter += 1;
  return `op_${Date.now().toString(36)}_${counter.toString(36)}`;
};

export interface CreateOperationParams {
  id?: string;
  seq: number;
  type: OperationType;
  regionId: RegionId;
  brushId: BrushId;
  color: string;
  size: number;
  points: NormPoint[];
  timestamp?: number;
  idFactory?: IdFactory;
  now?: () => number;
}

export function createOperation(params: CreateOperationParams): DrawOperation {
  const idFactory = params.idFactory ?? defaultIdFactory;
  const now = params.now ?? (() => Date.now());
  return {
    id: params.id ?? idFactory(),
    seq: params.seq,
    type: params.type,
    regionId: params.regionId,
    brushId: params.brushId,
    color: params.color,
    size: params.size,
    points: params.points.map((p) => ({ x: p.x, y: p.y })),
    timestamp: params.timestamp ?? now(),
  };
}

const REGION_SET = new Set<string>(REGION_IDS);

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isValidPoint(v: unknown): v is NormPoint {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as Record<string, unknown>;
  return isFiniteNumber(p.x) && isFiniteNumber(p.y);
}

/** Full structural + semantic validation of a single operation. */
export function isValidOperation(v: unknown): v is DrawOperation {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id.length === 0) return false;
  if (!isFiniteNumber(o.seq)) return false;
  if (o.type !== 'paint' && o.type !== 'erase') return false;
  if (typeof o.regionId !== 'string' || !REGION_SET.has(o.regionId)) return false;
  if (o.brushId !== 'marker') return false;
  if (typeof o.color !== 'string') return false;
  if (!isFiniteNumber(o.size) || o.size <= 0 || o.size > 1) return false;
  if (!Array.isArray(o.points) || o.points.length === 0) return false;
  if (!o.points.every(isValidPoint)) return false;
  if (!isFiniteNumber(o.timestamp)) return false;
  return true;
}

/** Serialize an operation to a JSON-safe plain object (stable key order). */
export function serializeOperation(op: DrawOperation): Record<string, unknown> {
  return {
    id: op.id,
    seq: op.seq,
    type: op.type,
    regionId: op.regionId,
    brushId: op.brushId,
    color: op.color,
    size: op.size,
    points: op.points.map((p) => ({ x: p.x, y: p.y })),
    timestamp: op.timestamp,
  };
}

/** Parse + validate a single operation, or return null if invalid. */
export function deserializeOperation(v: unknown): DrawOperation | null {
  if (!isValidOperation(v)) return null;
  const o = v as DrawOperation;
  return {
    id: o.id,
    seq: o.seq,
    type: o.type,
    regionId: o.regionId,
    brushId: o.brushId,
    color: o.color,
    size: o.size,
    points: o.points.map((p) => ({ x: p.x, y: p.y })),
    timestamp: o.timestamp,
  };
}

/** Sort operations by their authoritative sequence number (ascending). */
export function sortBySeq(ops: readonly DrawOperation[]): DrawOperation[] {
  return [...ops].sort((a, b) => a.seq - b.seq);
}
